import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Button, Card, Field, Input, MonoLabel, StatusPill, cn } from '@/design-system';
import {
    describeOrderProblem,
    lifecycleSteps,
    ORDER_STATUS_LABELS,
    sentMilestones,
    type Order,
    type OrderAccess,
    type OrderProblem,
    type OrderStatusCode,
} from '@/entities/order';
import { formatDayTimeShort } from '@/shared/lib';
import { LifecycleStrip } from '@/shared/ui';

import { paths } from '@/app/router/paths';
import { inspectionsCopy } from '../copy';
import { useCancelOrder, useInvoiceOrder, useScheduleOrder, useTransitionOrder } from '../hooks';
import { canSchedule, nextActionFor } from '../nextAction';
import { surveyActionFor } from '../surveyAction';
import { problemText } from '../problemText';
import { OrderStatusPill } from './OrderStatusPill';
import { OrderSurveyAction } from './SurveyActionButton';
import { ProblemLine } from './ProblemLine';
import { ReopenSurveyButton } from './ReopenSurveyButton';
import { ScheduleForm } from './ScheduleForm';

export type OrderDetailLayout = 'bar' | 'panel';

export interface OrderDetailBarProps {
    order: Order;
    access: OrderAccess;
    /** `bar` plakt onderaan het scherm, `panel` staat in het uitschuifpaneel. */
    layout?: OrderDetailLayout;
    /** Alleen in `bar`: samengevouwen tot één regel. */
    collapsed?: boolean;
    onToggleCollapsed?: (next: boolean) => void;
    /** Alleen in `panel`: sluit het uitschuifpaneel. */
    onClose?: () => void;
}

type Mode =
    | { kind: 'overview' }
    | { kind: 'schedule' }
    | { kind: 'cancel' }
    | { kind: 'transition'; status: OrderStatusCode };

type Outcome = { tone: 'done'; message: string } | { tone: 'problem'; problem: OrderProblem };

/**
 * De detailbalk onder de werklijst: wat is dit, wat kun je er nu mee, wat ging
 * er al naar de klant, en waar staat de keuring in haar levenscyclus.
 *
 * Elke knop doet iets echts. Welke stappen kunnen komt uit
 * `allowed_transitions` van de API; een stap die inmiddels niet meer kan (409)
 * wordt een zin die zegt waar de keuring nu staat.
 *
 * De balk **plakt onderaan het scherm** via een flex-kolom, niet via `sticky`:
 * een sticky kind zou meeschuiven met het horizontale scrollen van de tabel.
 */
export function OrderDetailBar({
    order,
    access,
    layout = 'bar',
    collapsed = false,
    onToggleCollapsed,
    onClose,
}: OrderDetailBarProps) {
    const navigate = useNavigate();
    const bar = layout === 'bar';
    const [mode, setMode] = useState<Mode>({ kind: 'overview' });
    const [outcome, setOutcome] = useState<Outcome | null>(null);
    const [reason, setReason] = useState('');

    const schedule = useScheduleOrder();
    const transition = useTransitionOrder();
    const cancel = useCancelOrder();
    const invoice = useInvoiceOrder();

    const busy =
        schedule.isPending || transition.isPending || cancel.isPending || invoice.isPending;
    const next = nextActionFor(order, access);
    /* De opname heeft haar eigen knop; die gaat voor op de generieke volgende stap. */
    const survey = surveyActionFor(order, access);
    const sent = sentMilestones(order);
    const folded = bar && collapsed && mode.kind === 'overview';
    const metaLine = [
        order.number,
        order.statusLabel,
        order.serviceLabel,
        order.assignee?.name ?? inspectionsCopy.noInspector,
    ]
        .join(' · ')
        .toUpperCase();

    function fail(error: unknown) {
        setOutcome({ tone: 'problem', problem: describeOrderProblem(error) });
    }

    function runNext() {
        setOutcome(null);

        if (next.kind === 'schedule') {
            setMode({ kind: 'schedule' });
        } else if (next.kind === 'invoice') {
            invoice.mutate(order.uuid, {
                onSuccess: () =>
                    setOutcome({
                        tone: 'done',
                        message: inspectionsCopy.invoiceCreated(order.number),
                    }),
                onError: fail,
            });
        } else {
            navigate(next.to);
        }
    }

    function step(status: OrderStatusCode) {
        setOutcome(null);
        setMode(status === 'scheduled' ? { kind: 'schedule' } : { kind: 'transition', status });
    }

    const primary =
        survey.kind === 'none' ? (
            <Button variant="primary" size="md" disabled={busy} onClick={runNext}>
                {next.label}
            </Button>
        ) : (
            <OrderSurveyAction order={order} access={access} />
        );

    return (
        <section
            aria-label={inspectionsCopy.detailLabel}
            className={cn(
                'bg-[var(--surface-card)]',
                bar
                    ? 'max-h-[70vh] shrink-0 overflow-auto border-t border-[var(--border-default)] px-6 pt-[14px] pb-[18px]'
                    : 'flex flex-col gap-5 px-4 pt-4 pb-8',
            )}
        >
            {!bar && onClose ? (
                <div className="flex justify-end">
                    <Button variant="ghost" onClick={onClose}>
                        {inspectionsCopy.detailClose}
                    </Button>
                </div>
            ) : null}

            {folded ? (
                <div className="flex flex-wrap items-center gap-3">
                    <span className="min-w-0 truncate font-[family-name:var(--font-display)] text-[16px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                        {order.property.shortAddress}
                    </span>
                    <OrderStatusPill order={order} />
                    <div className="min-w-[8px] flex-1" />
                    {primary}
                    <CollapseToggle collapsed onToggle={onToggleCollapsed} />
                </div>
            ) : (
                <div className={cn('flex flex-wrap gap-5', !bar && 'flex-col')}>
                    <div className="min-w-0 flex-[1_1_420px]">
                        <div className="mb-3 flex flex-wrap items-baseline gap-3">
                            <span className="font-[family-name:var(--font-display)] text-[16px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                                {order.property.shortAddress}
                            </span>
                            <MonoLabel size={11} tone="muted">
                                {metaLine}
                            </MonoLabel>
                            {bar ? (
                                <>
                                    <div className="min-w-[8px] flex-1" />
                                    <CollapseToggle
                                        collapsed={false}
                                        onToggle={onToggleCollapsed}
                                    />
                                </>
                            ) : null}
                        </div>

                        {mode.kind === 'schedule' ? (
                            <Card padding="md" radius={9} className="max-w-[460px]">
                                <h3 className="m-0 mb-2 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                                    {inspectionsCopy.scheduleTitle}
                                </h3>
                                <ScheduleForm
                                    order={order}
                                    saving={schedule.isPending}
                                    error={
                                        schedule.error
                                            ? problemText(describeOrderProblem(schedule.error))
                                            : null
                                    }
                                    onCancel={() => setMode({ kind: 'overview' })}
                                    onSubmit={(input) =>
                                        schedule.mutate(
                                            { orderUuid: order.uuid, ...input },
                                            {
                                                onSuccess: (updated) => {
                                                    setMode({ kind: 'overview' });
                                                    setOutcome({
                                                        tone: 'done',
                                                        message: inspectionsCopy.scheduleDone(
                                                            updated.number,
                                                            updated.scheduledAt
                                                                ? formatDayTimeShort(
                                                                      updated.scheduledAt,
                                                                  )
                                                                : '',
                                                        ),
                                                    });
                                                },
                                            },
                                        )
                                    }
                                />
                            </Card>
                        ) : mode.kind === 'cancel' ? (
                            <Card
                                variant="warn"
                                padding="md"
                                radius={9}
                                className="max-w-[520px]"
                                role="group"
                                aria-label={inspectionsCopy.cancelTitle(order.number)}
                            >
                                <p className="m-0 text-[13px] leading-[1.5] text-[var(--warn-ink)]">
                                    {inspectionsCopy.cancelBody}
                                </p>
                                <Field
                                    label={inspectionsCopy.cancelReason}
                                    size="sm"
                                    className="mt-3"
                                    error={
                                        reason !== '' && reason.trim().length < 3
                                            ? inspectionsCopy.cancelReasonTooShort
                                            : undefined
                                    }
                                >
                                    <Input
                                        value={reason}
                                        placeholder={inspectionsCopy.cancelReasonPlaceholder}
                                        onChange={(event) => setReason(event.target.value)}
                                    />
                                </Field>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <Button
                                        variant="warn"
                                        size="sm"
                                        disabled={reason.trim().length < 3 || cancel.isPending}
                                        onClick={() =>
                                            cancel.mutate(
                                                { orderUuid: order.uuid, reason: reason.trim() },
                                                {
                                                    onSuccess: (updated) => {
                                                        setMode({ kind: 'overview' });
                                                        setReason('');
                                                        setOutcome({
                                                            tone: 'done',
                                                            message: inspectionsCopy.stepDone(
                                                                updated.number,
                                                                updated.statusLabel,
                                                            ),
                                                        });
                                                    },
                                                    onError: fail,
                                                },
                                            )
                                        }
                                    >
                                        {inspectionsCopy.cancelYes}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setMode({ kind: 'overview' })}
                                    >
                                        {inspectionsCopy.cancelNo}
                                    </Button>
                                </div>
                            </Card>
                        ) : mode.kind === 'transition' ? (
                            <Card
                                variant="accent"
                                padding="md"
                                radius={9}
                                className="max-w-[520px]"
                                role="group"
                                aria-label={inspectionsCopy.stepTo(
                                    ORDER_STATUS_LABELS[mode.status],
                                )}
                            >
                                <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-primary)]">
                                    {inspectionsCopy.stepConfirm(
                                        order.number,
                                        ORDER_STATUS_LABELS[mode.status],
                                    )}
                                </p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        disabled={transition.isPending}
                                        onClick={() =>
                                            transition.mutate(
                                                { orderUuid: order.uuid, status: mode.status },
                                                {
                                                    onSuccess: (updated) => {
                                                        setMode({ kind: 'overview' });
                                                        setOutcome({
                                                            tone: 'done',
                                                            message: inspectionsCopy.stepDone(
                                                                updated.number,
                                                                updated.statusLabel,
                                                            ),
                                                        });
                                                    },
                                                    onError: (error) => {
                                                        setMode({ kind: 'overview' });
                                                        fail(error);
                                                    },
                                                },
                                            )
                                        }
                                    >
                                        {inspectionsCopy.stepYes(ORDER_STATUS_LABELS[mode.status])}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setMode({ kind: 'overview' })}
                                    >
                                        {inspectionsCopy.stepNo}
                                    </Button>
                                </div>
                            </Card>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {primary}
                                {survey.kind === 'none' &&
                                next.kind === 'navigate' &&
                                next.to === paths.dossier(order.uuid) ? null : (
                                    <Button
                                        variant="outline"
                                        size="md"
                                        onClick={() => navigate(paths.dossier(order.uuid))}
                                    >
                                        {inspectionsCopy.detailOpenOrder}
                                    </Button>
                                )}
                                {canSchedule(order, access) &&
                                (survey.kind !== 'none' || next.kind !== 'schedule') ? (
                                    <Button
                                        variant="outline"
                                        size="md"
                                        disabled={busy}
                                        onClick={() => step('scheduled')}
                                    >
                                        {order.scheduledAt
                                            ? inspectionsCopy.actionReschedule
                                            : inspectionsCopy.actionSchedule}
                                    </Button>
                                ) : null}
                                {access.manage && order.allowedTransitions.includes('confirmed') ? (
                                    <Button
                                        variant="outline"
                                        size="md"
                                        disabled={busy}
                                        onClick={() => step('confirmed')}
                                    >
                                        {inspectionsCopy.actionConfirm}
                                    </Button>
                                ) : null}
                                {sent.report ? (
                                    <Button
                                        variant="outline"
                                        size="md"
                                        onClick={() => navigate(paths.clientPage(order.uuid))}
                                    >
                                        {inspectionsCopy.detailClientPage}
                                    </Button>
                                ) : null}
                                {/* Alleen als de API zegt dat deze persoon het mag, ook na verzending. */}
                                <ReopenSurveyButton order={order} />
                                <Button
                                    variant="outline"
                                    size="md"
                                    onClick={() =>
                                        navigate(`${paths.dossier(order.uuid)}#documenten`)
                                    }
                                >
                                    {inspectionsCopy.detailFiles}
                                </Button>
                                {access.manage && order.allowedTransitions.includes('cancelled') ? (
                                    <Button
                                        variant="outline"
                                        size="md"
                                        disabled={busy}
                                        onClick={() => {
                                            setOutcome(null);
                                            setMode({ kind: 'cancel' });
                                        }}
                                    >
                                        {inspectionsCopy.actionCancel}
                                    </Button>
                                ) : null}
                            </div>
                        )}

                        {outcome ? <OutcomeLine outcome={outcome} /> : null}

                        {order.status === 'cancelled' ? (
                            <p className="m-0 mt-3 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                                {inspectionsCopy.cancelledNote(order.cancelledReason)}
                            </p>
                        ) : null}
                    </div>

                    <div className={cn(bar && 'min-w-[240px] flex-[0_1_300px]')}>
                        <MonoLabel size={10} tone="muted" as="div" className="mb-2">
                            {inspectionsCopy.contactEyebrow}
                        </MonoLabel>
                        <div className="mb-[9px] text-[13px] text-[var(--text-primary)]">
                            {[order.client.name, order.client.typeLabel]
                                .filter(Boolean)
                                .join(' · ')}
                        </div>
                        <div className="flex flex-wrap gap-[6px]">
                            <SentPill
                                label={inspectionsCopy.sentConfirmation}
                                done={sent.confirmation}
                            />
                            <SentPill
                                label={inspectionsCopy.sentAppointment}
                                done={sent.appointment}
                            />
                            <SentPill label={inspectionsCopy.sentReport} done={sent.report} />
                            <SentPill label={inspectionsCopy.sentInvoice} done={sent.invoice} />
                        </div>
                    </div>

                    <div className={cn(bar && 'flex-[1_1_100%]')}>
                        <MonoLabel size={10} tone="muted" as="div" className="mb-2">
                            {inspectionsCopy.lifecycleEyebrow}
                        </MonoLabel>
                        <LifecycleStrip
                            steps={lifecycleSteps(order)}
                            label={inspectionsCopy.lifecycleEyebrow}
                            busy={busy}
                            stepLabel={(lifecycleStep) =>
                                inspectionsCopy.stepTo(lifecycleStep.label)
                            }
                            onStep={
                                access.manage || access.assign
                                    ? (status) => {
                                          if (
                                              status === 'scheduled' ? access.assign : access.manage
                                          ) {
                                              step(status);
                                          }
                                      }
                                    : undefined
                            }
                        />
                    </div>
                </div>
            )}
        </section>
    );
}

function OutcomeLine({ outcome }: { outcome: Outcome }) {
    if (outcome.tone === 'done') {
        return (
            <p
                role="status"
                className="m-0 mt-3 text-[13px] leading-[1.5] text-[var(--accent-dark)]"
            >
                {outcome.message}
            </p>
        );
    }

    return <ProblemLine problem={outcome.problem} className="mt-3" />;
}

function CollapseToggle({
    collapsed,
    onToggle,
}: {
    collapsed: boolean;
    onToggle?: (next: boolean) => void;
}) {
    if (!onToggle) {
        return null;
    }

    return (
        <Button
            variant="ghost"
            aria-expanded={!collapsed}
            onClick={() => onToggle(!collapsed)}
            className="shrink-0"
        >
            {collapsed ? inspectionsCopy.detailExpand : inspectionsCopy.detailCollapse}
        </Button>
    );
}

function SentPill({ label, done }: { label: string; done: boolean }) {
    return (
        <StatusPill size="sm" tone={done ? 'accent' : 'neutral'}>
            {`${label} ${done ? inspectionsCopy.sentYes : inspectionsCopy.sentNo}`}
        </StatusPill>
    );
}
